import React from 'react';
import { BrokerClientApi, ListApi, TemplatesApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { bfiShareTypes, brokerClientStatuses, bfiLogo } from '../common/SharedConstants';
import { RejectConfirmModal, ESignModal } from '../common/SharedComponents';
import EditStdLoanModal from '../loans/modals/EditStdLoanModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import store, { loanActions } from '@apply/adminStore';
import { RefreshEvents } from '../common/SharedEvents';
import LoanHistoryTable from './tables/LoanHistoryTable';
import { LoadPanel } from 'devextreme-react/load-panel';
import LinkLoanWithBClientModal from '../loans/modals/LinkLoanWithBClientModal';
import { SelectBox } from 'devextreme-react/select-box';
import { PrincipalEntitySelector } from '../borrowers/common/BorrowerComponents';
import { brokerClientActions } from '@apply/adminStore';
import { handleAdminError, handleAdminInfo, handleAdminSuccess, handleAdminWarning } from '../common/SharedFunctions';
import { NumericFormat } from 'react-number-format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faPlus, faDownload, faW, faLink } from '@fortawesome/free-solid-svg-icons';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { hasPermission } from '@apply/ui';
import { PermissionType } from '@apply/constants';
import { YesNoToggleButton } from '@apply/components';

const createHeaderText = 'Add New ICCA / Broker Client';
const editHeaderText = 'Edit ICCA / Broker Client';

const BrokerClientDetails = () => {
    const { brokerClientState, user } = useSelector((state) => state);
    const templatesApi = new TemplatesApi.TemplatesApi(applicationInsights);
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const listApi = new ListApi.ListApi(applicationInsights);

    const { dispatch } = store;

    const refreshEvents = new RefreshEvents();

    const [headerText, set_headerText] = React.useState();

    const [loadingMessage, set_loadingMessage] = React.useState('Loading...');

    const [openRejectConfirmModal, set_openRejectConfirmModal] = React.useState(false);
    const [brokerClientId, set_brokerClientId] = React.useState('');
    const [brokerClientStatus, set_brokerClientStatus] = React.useState();
    const [brokerId, set_brokerId] = React.useState('');
    const [broker, set_broker] = React.useState();
    const [signedById, set_setSignedById] = React.useState('');
    const [iccaFileName, set_iccaFileName] = React.useState(false);
    const [iccaDownloadUrl, set_iccaDownloadUrl] = React.useState();
    const [clientFeeType, set_clientFeeType] = React.useState('');
    const [startDate, set_startDate] = React.useState();
    const [terminationDate, set_terminationDate] = React.useState();
    const [borrowerPrincipalId, set_borrowerPrincipalId] = React.useState(0);
    const [borrowerEntityId, set_borrowerEntityId] = React.useState(0);
    const [bfiShare, set_bfiShare] = React.useState();
    const [bfiShareSelect, set_bfiShareSelect] = React.useState('');
    const [showCustomBfiShare, set_showCustomBfiShare] = React.useState(false);
    const [iccaHtml, set_iccaHtml] = React.useState();
    const [iccaFile, set_iccaFile] = React.useState(false);
    const [loans, set_loans] = React.useState();
    const [eSignFont, set_eSignFont] = React.useState();
    const [openESignModal, set_openESignModal] = React.useState(false);
    const [brokerList, set_brokerList] = React.useState([]);
    const [bfiEmployeeList, set_BfiEmployeeList] = React.useState([]);
    const [clientIsFlagged, set_clientIsFlagged] = React.useState(false);
    const [isManualBrokerClient, set_isManualBrokerClient] = React.useState(false);
    const [subjectPropertyInAnyStates, set_subjectPropertyInAnyStates] = React.useState();
    const [nmlsLicenseNumber, set_nmlsLicenseNumber] = React.useState();

    React.useEffect(() => {
        const broker = brokerList.find((b) => b.key === parseInt(brokerId));

        if (broker) {
            set_broker(broker);
        } else {
            set_broker(null);
        }
    }, [brokerId, brokerList]);

    React.useEffect(() => {
        const loadListsAsync = async () => {
            await _loadActiveBrokers();
            await _loadBfiEmployees();
        };
        loadListsAsync();
    }, []);

    React.useEffect(() => {
        if (!brokerClientState || !brokerClientState.openBrokerClientModal) {
            return;
        }
        const loadBrokerClientAsync = async () => {
            await _loadBrokerClient();
        };
        loadBrokerClientAsync();
    }, [brokerClientState.Id]);

    const _handleClose = () => {
        dispatch(
            brokerClientActions.setBrokerClientOpen({
                openBrokerClientModal: false,
            })
        );
    };

    const validateBrokerBorrowerCombo = async (id, brokerId, principalId, entityId) => {
        try {
            set_loadingMessage('Validating Broker/Borrower combination...');

            //Validate Broker/Borrower Combination
            const response = await brokerClientApi.validateBrokerBorrowerCombination(id, brokerId, principalId, entityId);
            if (response.statusCode !== 200) {
                return false;
            }

            if (!response.result) {
                handleAdminInfo('This combination of Broker and Borrower already exists!');
                return false;
            }
            return true;
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred! Please try again.');
            return false;
        } finally {
            set_loadingMessage('');
        }
    };

    const _onSubmit = async (event) => {
        const isOk = await saveBClient(event);
        if (isOk) {
            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess('Broker client saved!');
            _handleClose();
        }
    };

    const saveBClient = async (event) => {
        event.preventDefault();

        if (!borrowerPrincipalId || !borrowerEntityId || !brokerId) {
            return false;
        }

        const isComboUnique = await validateBrokerBorrowerCombo(brokerClientId, brokerId, borrowerPrincipalId, borrowerEntityId);
        if (!isComboUnique) {
            return false;
        }

        if (subjectPropertyInAnyStates == undefined) {
            handleAdminWarning('Please enter in all fields');
            return false;
        }

        if (subjectPropertyInAnyStates && (!nmlsLicenseNumber || nmlsLicenseNumber?.length < 4)) {
            handleAdminWarning('NMLS Number should be at least 4 digits');
            return false;
        }

        try {
            set_loadingMessage('Saving B-Client...');
            const formData = new FormData();

            if (brokerClientId) {
                formData.append('brokerClientId', brokerClientId);
            }
            if (eSignFont) {
                formData.append('fontId', eSignFont);
            }
            formData.append('brokerId', brokerId);
            formData.append('borrowerPersonId', borrowerPrincipalId);
            formData.append('borrowerEntityId', borrowerEntityId);
            formData.append('clientIsFlagged', clientIsFlagged);
            formData.append('signedById', signedById);
            formData.append('clientFeeType', clientFeeType);
            formData.append('startDate', new Date(startDate).toLocaleDateString('en-us'));
            formData.append('terminationDate', new Date(terminationDate).toLocaleDateString('en-us'));
            formData.append('bfiShare', bfiShare);
            if (iccaFile) {
                formData.append('iccaFile', iccaFile);
            }
            formData.append('subjectPropertyInState', subjectPropertyInAnyStates);
            if (!subjectPropertyInAnyStates) {
                formData.append('subjectPropertyStateNMLSNumber', '');
            } else {
                formData.append('subjectPropertyStateNMLSNumber', nmlsLicenseNumber);
            }

            const response = await brokerClientApi.updateBrokerClient(formData);
            if (response.statusCode !== 200) {
                return false;
            }
            return true;
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred! Please try again.');
            return false;
        } finally {
            set_loadingMessage('');
        }
    };

    const _loadBrokerClient = async () => {
        try {
            set_loadingMessage('Loading B-Client details...');

            let brokerClient = null;
            if (brokerClientState.brokerClientId) {
                brokerClient = (await brokerClientApi.getBrokerClient(brokerClientState.brokerClientId)).result;
            }

            set_headerText(brokerClient ? editHeaderText : createHeaderText);
            set_brokerClientId(brokerClient?.brokerClientId);

            set_brokerId(brokerClient?.brokerId || brokerClientState.brokerId);

            set_brokerClientStatus(brokerClient?.brokerClientStatus);
            set_clientIsFlagged(brokerClient?.clientIsFlagged ?? false);
            set_clientFeeType(brokerClient?.clientFeeType ?? '');
            set_borrowerPrincipalId(brokerClient?.borrowerPersonId);
            set_borrowerEntityId(brokerClient?.borrowerEntityId);
            set_loans(brokerClient?.loans);
            set_iccaFileName(brokerClient?.iccaFileName);
            set_iccaDownloadUrl(brokerClient?.iccaDownloadUrl);
            set_isManualBrokerClient(brokerClient?.isManualBrokerClient);

            if (brokerClient?.startDate) {
                set_startDate(new Date(brokerClient?.startDate));
            } else {
                set_startDate(null);
            }

            if (brokerClient?.terminationDate) {
                set_terminationDate(new Date(brokerClient?.terminationDate));
            } else {
                set_terminationDate(null);
            }

            set_bfiShare(brokerClient?.bfiShare);
            if (brokerClient?.bfiShare && bfiShareTypes.indexOf(brokerClient?.bfiShare) === -1) {
                set_bfiShareSelect('Other');
                set_showCustomBfiShare(true);
            } else if (brokerClient?.bfiShare) {
                set_bfiShareSelect(brokerClient.bfiShare);
                set_showCustomBfiShare(false);
            } else {
                set_bfiShareSelect('');
                set_showCustomBfiShare(false);
            }
            set_subjectPropertyInAnyStates(brokerClient?.subjectPropertyInState);
            set_nmlsLicenseNumber(brokerClient?.subjectPropertyStateNMLSNumber);
        } finally {
            set_loadingMessage('');
        }
    };

    const _loadActiveBrokers = async () => {
        const response = await listApi.getActiveBrokerList();

        if (response.statusCode !== 200) return;

        set_brokerList(response.result);
        refreshEvents.addRefreshListener(_loadBrokerClient);
    };

    const _loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_BfiEmployeeList(data);
    };

    const _bfiShareSelectChange = async (event) => {
        const selectValue = event.target.value;

        set_showCustomBfiShare(selectValue === 'Other');

        if (bfiShareTypes.includes(selectValue)) {
            set_bfiShare(selectValue);
            set_bfiShareSelect(selectValue);
        } else if (selectValue === 'Other') {
            set_bfiShareSelect('Other');
        } else {
            set_bfiShare(null);
            set_bfiShareSelect('');
        }
    };

    const _handleAcceptButton = async (event) => {
        const isOk = await saveBClient(event);
        if (!isOk) {
            return;
        }

        const postData = {
            brokerAgreementId: brokerClientId,
            logoUrl: bfiLogo,
        };

        const response = await templatesApi.countersignIccaHtml(postData);
        if (response.statusCode !== 200) return;

        const html = response.result;
        set_iccaHtml(html);
        set_openESignModal(true);
    };

    const _handleESignModalConfirm = async (font) => {
        try {
            set_loadingMessage('Saving...');
            const response = await brokerClientApi.acceptBrokerAgreement({
                brokerAgreementId: brokerClientId,
                logoUrl: bfiLogo,
                fontId: font.key,
            });

            if (response.statusCode !== 200) {
                throw new Error('Failed to accept Broker Agreement');
            }

            set_eSignFont(font.key);
            submitRef.current.click();
            refreshEvents.dispatchRefreshEvent();
            set_openRejectConfirmModal(false);
            set_openESignModal(false);

            handleAdminSuccess('Agreement has been Accepted successfully. PDF will be available for download shortly.');
            _handleClose();
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occured! Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const _handleESignModalCancel = () => {
        set_openESignModal(false);
    };

    const _handleRejectButton = () => {
        set_openRejectConfirmModal(true);
    };

    const _handleRejectConfirm = async (comment) => {
        try {
            const response = await brokerClientApi.rejectBrokerAgreement({
                id: brokerClientId,
                comment,
            });

            if (response.statusCode != 200) {
                throw new Error('Failed to reject B-Client');
            }

            refreshEvents.dispatchRefreshEvent();
            handleAdminSuccess('B-Client has been successfully rejected.');

            set_openRejectConfirmModal(false);
            _handleClose();
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const _handleRejectCancel = () => {
        set_openRejectConfirmModal(false);
    };

    const _handleAddLoanButton = () => {
        dispatch(
            loanActions.setEditStdLoanModal({
                editStdLoanModal: {
                    isOpen: true,
                    bClientId: brokerClientId,
                    showInsurance: false,
                },
            })
        );
    };

    const _onLinkLoanToBClientButton = () => {
        dispatch(
            loanActions.setLinkLoanWithBClientModal({
                linkLoanWithBClientModal: {
                    isOpen: true,
                    bClientId: brokerClientId,
                },
            })
        );
    };

    const downloadIcca = (event) => {
        event.preventDefault();
        window.location.href = iccaDownloadUrl;
    };

    const onBorrowerSelect = (principalId, entityId) => {
        set_borrowerPrincipalId(principalId);
        set_borrowerEntityId(entityId);
    };

    let closeButton;
    if (user.admin)
        closeButton = (
            <button
                type='button'
                className='button hollow'
                data-dismiss='modal'
                onClick={_handleClose}
            >
                Cancel
            </button>
        );

    const styles = {
        buttonMarginRight: {
            marginRight: '10px',
        },
        flexContainer: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        hidden: {
            display: 'none',
        },
        nowrap: {
            whiteSpace: 'nowrap',
        },
    };

    const onBrokerChange = (e) => {
        set_brokerId(e.value);
    };

    const renderBrokerOption = (e) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={e?.isFlagged ? faFlag : faW} />
                &nbsp;
                {e.brokerName} {e.businessName && <div>{e.businessName}</div>}
            </div>
        );
    };

    const showFileUpload =
        !brokerClientState.brokerClientId || // if brand new Broker Client
        (isManualBrokerClient && !iccaDownloadUrl);

    const submitRef = React.useRef(null);
    return (
        <>
            <form
                name='brokerClientForm'
                onSubmit={(event) => _onSubmit(event)}
            >
                <div
                    className='page-header'
                    role='banner'
                >
                    <h1 style={styles.nowrap}>{headerText}</h1>
                    <button
                        type='button'
                        className='close'
                        data-dismiss='modal'
                        aria-label='Close'
                        onClick={_handleClose}
                    >
                        <span aria-hidden='true'>&times;</span>
                    </button>
                </div>
                <div className='main-content no-search'>
                    <div
                        className='scrollable-content'
                        style={{ paddingTop: '10px' }}
                    >
                        <div className='standard-form'>
                            <h2 className='decorated'>Broker Information</h2>
                            <div
                                className='row'
                                style={{ marginBottom: '0' }}
                            >
                                <div className='columns small-12 medium-5'>
                                    <label htmlFor='brokerId'>
                                        Name
                                        <span className='req'>*</span>
                                    </label>
                                    <SelectBox
                                        placeholder='Select...'
                                        valueExpr='key'
                                        displayExpr='brokerName'
                                        value={brokerId}
                                        items={brokerList}
                                        onValueChanged={onBrokerChange}
                                        itemRender={renderBrokerOption}
                                        searchEnabled={true}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Name of broker for new client</span>
                                    </span>
                                </div>
                                <div className='columns small-12 medium-7'>
                                    <label htmlFor='firm'>Firm</label>
                                    <input
                                        id='firm'
                                        type='text'
                                        value={broker?.businessName ?? 'No Firm'}
                                        readOnly
                                    />
                                </div>
                                <div className='columns small-12 medium-3'>
                                    <label htmlFor='clientFeeType'>Client Fee %</label>
                                    <NumericFormat
                                        thousandSeparator=','
                                        prefix=''
                                        suffix='%'
                                        id='clientFeeType'
                                        placeholder=''
                                        value={clientFeeType}
                                        decimalScale={2}
                                        allowNegative={false}
                                        onValueChange={(values) => {
                                            set_clientFeeType(values.floatValue ?? '');
                                        }}
                                        min={0}
                                        max={1}
                                        required={true}
                                        isAllowed={(values, sourceInfo) => {
                                            const value = values?.floatValue;
                                            return value === undefined || value === null || (value >= 0 && value <= 1);
                                        }}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Fee type for broker client.</span>
                                    </span>
                                </div>

                                {headerText === createHeaderText && (
                                    <div className='columns small-12 medium-5'>
                                        <label htmlFor='signedById'>
                                            Signed By
                                            <span className='req'>*</span>
                                        </label>
                                        <select
                                            id='signedById'
                                            value={signedById}
                                            onChange={(event) => set_setSignedById(event.target.value)}
                                            required
                                        >
                                            <option value=''>Select</option>
                                            {bfiEmployeeList.map((employee) => {
                                                return (
                                                    <option
                                                        key={employee.key}
                                                        value={employee.key}
                                                    >
                                                        {employee.name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <span className='helper'>
                                            <span className='tooltip-trigger'>
                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                            </span>
                                            <span className='text'>Name of broker for new client</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div
                                className='row'
                                style={{ marginBottom: '0' }}
                            >
                                {broker?.isFlagged && (
                                    <div className='columns small-12 medium-4'>
                                        <span>
                                            <i
                                                style={{ color: 'darkred' }}
                                                title='Broker is Flagged'
                                            >
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                            <span
                                                className='text'
                                                style={{ paddingLeft: 8 }}
                                            >
                                                Broker is Flagged
                                            </span>
                                        </span>
                                    </div>
                                )}
                                {user.admin && (
                                    <div className='columns small-12 medium-6'>
                                        <input
                                            id='clientIsFlagged'
                                            type='checkbox'
                                            checked={clientIsFlagged === true}
                                            onChange={(event) => set_clientIsFlagged(event.target.checked)}
                                        />
                                        <label htmlFor='clientIsFlagged'>B-Client is Flagged</label>{' '}
                                        {clientIsFlagged && (
                                            <i style={{ color: 'darkred' }}>
                                                <FontAwesomeIcon icon={faFlag} />
                                            </i>
                                        )}
                                    </div>
                                )}
                            </div>

                            <h2 className='decorated'>ICCA Information</h2>
                            <div
                                className='row'
                                style={{ marginBottom: '0' }}
                            >
                                <div className='columns small-12 medium-6'>
                                    <label htmlFor='startDate'>
                                        Effective Date
                                        <span className='req'>*</span>
                                    </label>
                                    <DatePicker
                                        id='startDate'
                                        selected={startDate}
                                        onChange={(date) => set_startDate(date)}
                                        required={true}
                                    />
                                </div>
                                <div className='columns small-12 medium-6'>
                                    <label htmlFor='terminationDate'>
                                        Termination Date
                                        <span className='req'>*</span>
                                    </label>
                                    <DatePicker
                                        id='terminationDate'
                                        selected={terminationDate}
                                        onChange={(date) => set_terminationDate(date)}
                                        required={true}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>
                                            The full expiration of the ICCA, usually 2 years after the expiration date.
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <h2 className='decorated'>Principal & Entity</h2>
                            <div
                                className='row'
                                style={{ marginBottom: '0' }}
                            >
                                <div className='columns small-12 medium-12'>
                                    <PrincipalEntitySelector
                                        onSelect={onBorrowerSelect}
                                        principalId={borrowerPrincipalId}
                                        entityId={borrowerEntityId}
                                        disabled={false}
                                        addressRequired={false}
                                    />
                                </div>
                                <div className='columns small-12 medium-6'>
                                    <label htmlFor='bfiShare'>
                                        BFI Share
                                        <span className='req'>*</span>
                                    </label>
                                    <select
                                        id='bfiShare'
                                        value={bfiShareSelect}
                                        onChange={_bfiShareSelectChange}
                                        required
                                    >
                                        {bfiShareTypes.map((type) => {
                                            return (
                                                <option
                                                    key={type}
                                                    value={type}
                                                >
                                                    {type}
                                                </option>
                                            );
                                        })}
                                        <option
                                            key='Other'
                                            value='Other'
                                        >
                                            Other
                                        </option>
                                    </select>
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>
                                            Standard is 1% of the first deal and 0.5% thereafter. High Volume is Standard but on
                                            rentals, 2% then 1%. Other to be described in free text.
                                        </span>
                                    </span>
                                </div>
                                <div className='columns small-12 medium-6'>
                                    {showCustomBfiShare && (
                                        <>
                                            <label htmlFor='bfiShare'>
                                                Other BFI Share
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='bfiShare'
                                                type='text'
                                                value={bfiShare}
                                                onChange={(event) => set_bfiShare(event.target.value)}
                                                required
                                            />
                                        </>
                                    )}
                                </div>
                                <div className='columns small-12 medium-6'>
                                    {iccaDownloadUrl && (
                                        <>
                                            <label htmlFor='iccaFile'>Signed ICCA File Uploaded:</label>
                                            <p>{iccaFileName}</p>
                                            <button
                                                className='gridButton'
                                                onClick={downloadIcca}
                                            >
                                                <FontAwesomeIcon icon={faDownload} /> Download
                                            </button>
                                        </>
                                    )}
                                    {showFileUpload && (
                                        <>
                                            <label htmlFor='iccaFile'>Upload fully signed ICCA File:</label>
                                            <input
                                                type='file'
                                                id='iccaFile'
                                                onChange={(event) => set_iccaFile(event.target.files[0])}
                                                required
                                            />
                                        </>
                                    )}
                                </div>
                                <div className='columns small-12 medium-12'>
                                    <label htmlFor='subjectPropertyInState'>
                                        Will the subject property be in Arizona?
                                        <span className='req'>*</span>
                                    </label>
                                    <YesNoToggleButton
                                        id='subjectPropertyInState'
                                        value={subjectPropertyInAnyStates}
                                        onItemClick={(newValue) =>
                                            // @ts-ignore
                                            set_subjectPropertyInAnyStates(newValue.itemData?.id === 'Yes')
                                        }
                                        required={true}
                                    />
                                    {subjectPropertyInAnyStates && (
                                        <>
                                            <br />
                                            <label htmlFor='nmlsNumber'>
                                                Arizona NMLS License Number
                                                <span className='req'>*</span>
                                            </label>
                                            <input
                                                id='nmlsNumber'
                                                type='text'
                                                value={nmlsLicenseNumber}
                                                // @ts-ignore
                                                onChange={(event) => set_nmlsLicenseNumber(event.target.value)}
                                                required
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                            {brokerClientState.brokerClientId > 0 && (
                                <>
                                    <h2 className='decorated'>Closed Loan History</h2>
                                    <div
                                        className='primary-actions'
                                        style={{ float: 'right' }}
                                    >
                                        <button
                                            type='button'
                                            className='gridButton'
                                            onClick={_handleAddLoanButton}
                                        >
                                            <FontAwesomeIcon icon={faPlus} /> Add Loan
                                        </button>
                                        <br />
                                        <button
                                            type='button'
                                            className='gridButton'
                                            onClick={_onLinkLoanToBClientButton}
                                        >
                                            <FontAwesomeIcon icon={faLink} />
                                            &nbsp;Link Loan
                                        </button>
                                    </div>
                                    <div
                                        className='row'
                                        style={styles.flexContainer}
                                    >
                                        <LoanHistoryTable
                                            loans={loans}
                                            masterNotes={true}
                                        />
                                        <LoanHistoryTable
                                            loans={loans}
                                            masterNotes={false}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className='button-footer'>
                            {brokerClientStatus === brokerClientStatuses.pending ? (
                                <>
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='button'
                                        className='button primary'
                                        onClick={_handleAcceptButton}
                                        disabled={!hasPermission(PermissionType.BClientApproveReject)}
                                    >
                                        Sign and Accept
                                    </button>
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='button'
                                        className='button redButton'
                                        onClick={_handleRejectButton}
                                        disabled={!hasPermission(PermissionType.BClientApproveReject)}
                                    >
                                        Reject
                                    </button>
                                    <button
                                        style={styles.hidden}
                                        type='submit'
                                        className='button primary'
                                        ref={submitRef}
                                    >
                                        Submit
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        style={styles.buttonMarginRight}
                                        type='submit'
                                        className='button primary'
                                    >
                                        Submit
                                    </button>
                                    {closeButton}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </form>
            <ESignModal
                isOpen={openESignModal}
                confirmCallback={(font) => _handleESignModalConfirm(font)}
                cancelCallback={() => _handleESignModalCancel()}
                signatureText={`${user.firstName} ${user.lastName}`}
                agreementHtml={`<div style="color: black">${iccaHtml}</div>`}
            />
            <RejectConfirmModal
                isOpen={openRejectConfirmModal}
                confirmCallback={_handleRejectConfirm}
                cancelCallback={_handleRejectCancel}
            />
            <EditStdLoanModal />
            <LinkLoanWithBClientModal />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default BrokerClientDetails;
